import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, Home, RotateCcw, ChevronRight, Trophy, Volume2, VolumeX, Crown, Star } from 'lucide-react';

const GamePage = () => {
  const navigate = useNavigate();
  const {
    gameMode,
    player1Name,
    player2Name,
    currentPlayer,
    player1Score,
    player2Score,
    player1Balls,
    player2Balls,
    gameStatus,
    currentPage,
    lastDigit,
    isPlayerOut,
    isFlipping,
    soundEnabled,
    setSoundEnabled,
    flipBook,
    nextPlayer,
    resetGame,
  } = useGame();

  // Redirect if no game is active
  useEffect(() => {
    if (gameStatus === 'idle' && !gameMode) {
      navigate('/');
    }
  }, [gameStatus, gameMode, navigate]);

  // Auto-play for computer
  useEffect(() => {
    if (gameMode === 'pvc' && currentPlayer === 2 && gameStatus === 'playing' && !isPlayerOut && !isFlipping) {
      const timer = setTimeout(() => {
        flipBook();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [gameMode, currentPlayer, gameStatus, isPlayerOut, isFlipping, flipBook]);

  const handleFlip = useCallback(() => {
    if (isFlipping || isPlayerOut) return;
    flipBook();
  }, [isFlipping, isPlayerOut, flipBook]);

  const handleNextPlayer = useCallback(() => {
    nextPlayer();
  }, [nextPlayer]);

  const handlePlayAgain = useCallback(() => {
    resetGame();
    navigate('/');
  }, [resetGame, navigate]);

  const getWinner = () => {
    if (player1Score > player2Score) return player1Name;
    if (player2Score > player1Score) return player2Name;
    return 'Tie';
  };

  const currentPlayerName = currentPlayer === 1 ? player1Name : player2Name;
  const currentBalls = currentPlayer === 1 ? player1Balls : player2Balls;

  // Winner screen with trophy
  if (gameStatus === 'finished') {
    const winner = getWinner();
    const isTie = winner === 'Tie';
    const winnerScore = player1Score > player2Score ? player1Score : player2Score;

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Celebration particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#fbbf24', '#f59e0b', '#eab308', '#facc15'][i % 4],
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>

        <Card className="bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-amber-500/50 max-w-lg w-full shadow-2xl shadow-amber-500/20">
          <CardContent className="p-10 text-center relative">
            {/* Trophy with winner name */}
            <div className="mb-8 relative">
              {/* Glow effect */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />
              
              {/* Trophy */}
              <div className="relative">
                <div className="w-36 h-36 mx-auto relative">
                  {/* Trophy cup */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      {/* Crown on top */}
                      {!isTie && (
                        <Crown className="w-10 h-10 text-amber-400 absolute -top-8 left-1/2 -translate-x-1/2 animate-bounce" />
                      )}
                      {/* Trophy icon */}
                      <div className="w-28 h-28 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-600 rounded-t-full flex items-center justify-center shadow-lg relative">
                        <Trophy className="w-14 h-14 text-amber-900" />
                        {/* Stars around trophy */}
                        <Star className="w-5 h-5 text-yellow-300 absolute -left-3 top-2 animate-pulse fill-yellow-300" />
                        <Star className="w-4 h-4 text-yellow-300 absolute -right-2 top-4 animate-pulse fill-yellow-300" style={{ animationDelay: '0.5s' }} />
                        <Star className="w-3 h-3 text-yellow-300 absolute -left-1 bottom-4 animate-pulse fill-yellow-300" style={{ animationDelay: '1s' }} />
                      </div>
                      {/* Trophy base */}
                      <div className="w-16 h-4 bg-gradient-to-b from-amber-600 to-amber-700 mx-auto rounded-b" />
                      <div className="w-20 h-3 bg-gradient-to-b from-amber-700 to-amber-800 mx-auto rounded-b" />
                      <div className="w-24 h-2 bg-amber-900 mx-auto rounded-b" />
                    </div>
                  </div>
                </div>
                
                {/* Winner name plate */}
                <div className="mt-4 relative">
                  <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 px-8 py-3 rounded-lg shadow-lg border-2 border-amber-400 inline-block">
                    <p className="text-amber-950 font-bold text-2xl tracking-wide">
                      {isTie ? "IT'S A TIE!" : winner.toUpperCase()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">
              {isTie ? "Both Champions!" : "🏆 Champion! 🏆"}
            </h2>
            <p className="text-amber-300 mb-6">
              {isTie ? 'What an incredible match!' : `Victory with ${winnerScore} runs!`}
            </p>

            {/* Score cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className={`p-5 rounded-xl transition-all ${player1Score >= player2Score ? 'bg-gradient-to-br from-amber-500/30 to-amber-600/20 border-2 border-amber-400 shadow-lg shadow-amber-500/20' : 'bg-slate-700/50 border border-slate-600'}`}>
                {player1Score > player2Score && <Crown className="w-5 h-5 text-amber-400 mx-auto mb-1" />}
                <p className="text-slate-300 text-sm mb-1">{player1Name}</p>
                <p className="text-4xl font-bold text-white">{player1Score}</p>
                <p className="text-slate-400 text-xs mt-2">{player1Balls.length} balls faced</p>
              </div>
              <div className={`p-5 rounded-xl transition-all ${player2Score >= player1Score ? 'bg-gradient-to-br from-amber-500/30 to-amber-600/20 border-2 border-amber-400 shadow-lg shadow-amber-500/20' : 'bg-slate-700/50 border border-slate-600'}`}>
                {player2Score > player1Score && <Crown className="w-5 h-5 text-amber-400 mx-auto mb-1" />}
                <p className="text-slate-300 text-sm mb-1">{player2Name}</p>
                <p className="text-4xl font-bold text-white">{player2Score}</p>
                <p className="text-slate-400 text-xs mt-2">{player2Balls.length} balls faced</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handlePlayAgain}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg shadow-lg shadow-amber-500/30"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Play Again
              </Button>
              <Button
                onClick={() => navigate('/history')}
                variant="outline"
                className="border-slate-500 text-slate-300 hover:bg-slate-700/50 py-6 px-6"
              >
                History
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-green-900 relative overflow-hidden">
      {/* Stadium Lines */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] border-4 border-white rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] border-4 border-white rounded-full" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => { resetGame(); navigate('/'); }}
            className="text-white hover:bg-white/10"
          >
            <Home className="w-5 h-5 mr-2" />
            Home
          </Button>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-emerald-500 text-emerald-300">
              {gameMode === 'pvp' ? 'PvP' : 'vs Computer'}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/10"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Scoreboard */}
        <div className="grid grid-cols-2 gap-4 mb-4 max-w-2xl mx-auto">
          <Card className={`transition-all duration-300 ${currentPlayer === 1 ? 'bg-emerald-700/80 border-amber-400 shadow-lg shadow-amber-400/20' : 'bg-emerald-800/50 border-emerald-600/50'}`}>
            <CardContent className="p-4 text-center">
              <p className="text-emerald-200 text-sm mb-1">{player1Name}</p>
              <p className="text-4xl font-bold text-white">{player1Score}</p>
              {currentPlayer === 2 && player1Balls.length > 0 && (
                <Badge className="mt-2 bg-red-500/80">OUT</Badge>
              )}
            </CardContent>
          </Card>
          <Card className={`transition-all duration-300 ${currentPlayer === 2 ? 'bg-emerald-700/80 border-amber-400 shadow-lg shadow-amber-400/20' : 'bg-emerald-800/50 border-emerald-600/50'}`}>
            <CardContent className="p-4 text-center">
              <p className="text-emerald-200 text-sm mb-1">{player2Name}</p>
              <p className="text-4xl font-bold text-white">{player2Score}</p>
              {currentPlayer === 1 && (
                <Badge className="mt-2 bg-emerald-600">Waiting</Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Current Player Indicator */}
        <div className="text-center mb-4">
          <p className="text-emerald-300 text-sm">Now Playing</p>
          <h2 className="text-2xl font-bold text-white">{currentPlayerName}'s Turn</h2>
          {currentPlayer === 2 && player1Score > 0 && (
            <p className="text-amber-400 text-sm mt-1">Target: {player1Score + 1} runs to win</p>
          )}
        </div>

        {/* GOTHIC BOOK */}
        <div className="flex justify-center mb-6">
          <div 
            className={`relative cursor-pointer transition-all duration-500 ${isFlipping ? 'scale-95 rotate-1' : 'hover:scale-105 hover:-rotate-1'}`}
            onClick={handleFlip}
          >
            {/* Book glow effect */}
            <div className="absolute -inset-4 bg-amber-500/20 rounded-3xl blur-xl opacity-50" />
            
            {/* Main Gothic Book */}
            <div className={`relative w-72 h-96 ${isFlipping ? 'animate-pulse' : ''}`}>
              {/* Book shadow */}
              <div className="absolute -bottom-4 left-4 right-4 h-8 bg-black/40 blur-xl rounded-full" />
              
              {/* Back cover decorative layer */}
              <div className="absolute inset-0 translate-x-2 translate-y-1 bg-gradient-to-br from-stone-800 to-stone-900 rounded-r-lg rounded-l-sm" />
              
              {/* Book cover */}
              <div className="absolute inset-0 bg-gradient-to-br from-stone-700 via-stone-800 to-stone-900 rounded-r-lg rounded-l-sm shadow-2xl border border-stone-600/50">
                
                {/* Leather texture overlay */}
                <div className="absolute inset-0 opacity-30 rounded-r-lg" 
                  style={{ 
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` 
                  }} 
                />
                
                {/* Ornate corner decorations */}
                <div className="absolute top-3 left-3 w-12 h-12 border-l-2 border-t-2 border-amber-600/70 rounded-tl-lg" />
                <div className="absolute top-3 right-3 w-12 h-12 border-r-2 border-t-2 border-amber-600/70 rounded-tr-lg" />
                <div className="absolute bottom-3 left-3 w-12 h-12 border-l-2 border-b-2 border-amber-600/70 rounded-bl-lg" />
                <div className="absolute bottom-3 right-3 w-12 h-12 border-r-2 border-b-2 border-amber-600/70 rounded-br-lg" />
                
                {/* Center ornamental frame */}
                <div className="absolute inset-8 border-2 border-amber-700/50 rounded-lg">
                  <div className="absolute inset-2 border border-amber-600/30 rounded" />
                </div>
                
                {/* Gothic spine detail */}
                <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-stone-900 via-stone-800 to-transparent rounded-l-sm">
                  <div className="absolute top-8 left-1 w-4 h-1 bg-amber-700/60 rounded" />
                  <div className="absolute top-16 left-1 w-4 h-1 bg-amber-700/60 rounded" />
                  <div className="absolute bottom-16 left-1 w-4 h-1 bg-amber-700/60 rounded" />
                  <div className="absolute bottom-8 left-1 w-4 h-1 bg-amber-700/60 rounded" />
                </div>
                
                {/* Metal corner clasps */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-700 rounded-br-lg rounded-tl-sm shadow-md" />
                <div className="absolute bottom-2 right-2 w-6 h-6 bg-gradient-to-br from-amber-500 to-amber-700 rounded-tr-lg rounded-bl-sm shadow-md" />
                
                {/* Inner pages area */}
                <div className="absolute inset-10 left-12 bg-gradient-to-br from-amber-50 via-amber-100 to-orange-50 rounded shadow-inner overflow-hidden">
                  {/* Page texture lines */}
                  <div className="absolute inset-0 opacity-20">
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className="h-px bg-amber-300 my-4" style={{ marginLeft: `${i % 2 === 0 ? 8 : 12}px` }} />
                    ))}
                  </div>
                  
                  {/* Aged paper effect */}
                  <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-amber-200/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-amber-200/50 to-transparent" />
                  
                  {currentPage !== null ? (
                    <div className="h-full flex flex-col items-center justify-center relative z-10 p-4">
                      <p className="text-amber-600/80 text-sm font-serif italic mb-1">Page</p>
                      <p className="text-6xl font-bold text-stone-800 mb-4 font-serif" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}>
                        {currentPage}
                      </p>
                      <div className="w-16 h-0.5 bg-amber-400/50 mb-4" />
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-stone-600 text-sm font-serif">Last digit:</span>
                        <span className={`text-3xl font-bold font-serif ${isOut(lastDigit) ? 'text-red-600' : 'text-emerald-700'}`}>
                          {lastDigit}
                        </span>
                      </div>
                      {isPlayerOut && (
                        <div className="bg-red-600 text-white px-6 py-2 rounded-full font-bold text-lg animate-pulse shadow-lg">
                          OUT!
                        </div>
                      )}
                      {!isPlayerOut && lastDigit !== null && (
                        <div className="bg-emerald-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                          +{lastDigit} runs!
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center relative z-10">
                      <div className="w-16 h-16 mb-4 relative">
                        <BookOpen className="w-16 h-16 text-amber-400/80" />
                        <div className="absolute inset-0 animate-ping">
                          <BookOpen className="w-16 h-16 text-amber-400/30" />
                        </div>
                      </div>
                      <p className="text-stone-600 text-base text-center px-6 font-serif italic">
                        {gameMode === 'pvc' && currentPlayer === 2 
                          ? 'The ancient book reveals...' 
                          : 'Tap to reveal your fate!'}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Book title embossing */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 text-amber-600/80 text-xs font-serif tracking-widest">
                  BOOK OF CRICKET
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="max-w-md mx-auto space-y-3">
          {!isPlayerOut && !(gameMode === 'pvc' && currentPlayer === 2) && (
            <Button
              onClick={handleFlip}
              disabled={isFlipping}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg shadow-lg shadow-amber-500/30 disabled:opacity-50"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              {isFlipping ? 'Flipping...' : 'Flip the Book'}
            </Button>
          )}

          {isPlayerOut && (
            <Button
              onClick={handleNextPlayer}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-6 text-lg"
            >
              <ChevronRight className="w-5 h-5 mr-2" />
              {currentPlayer === 1 ? `${player2Name}'s Turn` : 'See Results'}
            </Button>
          )}
        </div>

        {/* Ball History */}
        <div className="max-w-md mx-auto mt-6">
          <p className="text-emerald-300 text-sm mb-3 text-center">This Innings</p>
          <div className="flex flex-wrap justify-center gap-2">
            {currentBalls.map((ball, idx) => (
              <div
                key={idx}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-lg transition-all duration-300 ${
                  ball === 0 || ball === 8
                    ? 'bg-gradient-to-br from-red-500 to-red-600'
                    : ball === 6
                      ? 'bg-gradient-to-br from-purple-500 to-purple-600'
                      : ball === 4
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600'
                        : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                }`}
              >
                {ball === 0 || ball === 8 ? 'W' : ball}
              </div>
            ))}
            {currentBalls.length === 0 && (
              <p className="text-emerald-400 text-sm">No balls played yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function used in render
const isOut = (digit) => digit === 0 || digit === 8;

export default GamePage;
